import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Alert,
  LinearProgress,
  Skeleton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Business,
  People,
  Assignment,
  TrendingUp,
  TrendingDown,
  VerifiedUser,
  CloudUpload,
  Assessment,
  GroupWork,
  Add,
  Flight,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  ArrowForward,
  CalendarToday,
  Speed,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EnterpriseStats {
  totalApplications: number;
  activeVisas: number;
  teamMembers: number;
  complianceScore: number;
  totalApplicationsTrend: number;
  activeVisasTrend: number;
  teamMembersTrend: number;
  complianceScoreTrend: number;
}

interface MonthlyApplication {
  month: string;
  applications: number;
}

interface StatusSlice {
  name: string;
  value: number;
  color: string;
}

interface RecentApplication {
  id: string;
  applicantName: string;
  destination: string;
  visaType: string;
  status: string;
  submittedAt: string;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  applicationCount: number;
  avatarColor: string;
}

// ---------------------------------------------------------------------------
// Helpers / constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
  under_review: 'info',
  draft: 'default',
  processing: 'info',
};

const PIE_COLORS = ['#1273E5', '#29B848', '#FF9800', '#E53935', '#9C27B0', '#00BCD4'];

const STATUS_COLOR_MAP: Record<string, string> = {
  approved: '#29B848',
  pending: '#FF9800',
  under_review: '#1273E5',
  rejected: '#E53935',
  draft: '#9E9E9E',
  processing: '#00BCD4',
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const todayFormatted = () =>
  new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const KpiCard: React.FC<{
  title: string;
  value: string | number;
  trend: number;
  icon: React.ReactNode;
  gradient: string;
}> = ({ title, value, trend, icon, gradient }) => (
  <Card sx={{ background: gradient, borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" mt={1}>
            {trend >= 0 ? (
              <TrendingUp sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, mr: 0.5 }} />
            )}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
              {trend >= 0 ? '+' : ''}
              {trend}% vs last month
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const KpiSkeleton: React.FC = () => (
  <Card sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={40} />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="50%" />
    </CardContent>
  </Card>
);

const QuickActionCard: React.FC<{ title: string; description: string; icon: React.ReactNode; onClick: () => void }> = ({
  title,
  description,
  icon,
  onClick,
}) => (
  <Card
    sx={{
      borderRadius: 3,
      height: '100%',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      bgcolor: 'rgba(15, 23, 42, 0.6)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Avatar sx={{ bgcolor: 'rgba(18, 115, 229, 0.15)', mb: 2, width: 48, height: 48 }}>{icon}</Avatar>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    </CardActionArea>
  </Card>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const EnterpriseDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { showError } = useNotification();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EnterpriseStats>({
    totalApplications: 0,
    activeVisas: 0,
    teamMembers: 0,
    complianceScore: 0,
    totalApplicationsTrend: 0,
    activeVisasTrend: 0,
    teamMembersTrend: 0,
    complianceScoreTrend: 0,
  });
  const [monthlyData, setMonthlyData] = useState<MonthlyApplication[]>([]);
  const [statusData, setStatusData] = useState<StatusSlice[]>([]);
  const [recentApps, setRecentApps] = useState<RecentApplication[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // --- Stats ---
      let statsLoaded = false;
      try {
        const res = await fetch(`${API_BASE_URL}/enterprise/dashboard`, { headers });
        if (res.ok) {
          const body = await res.json();
          const d = body.data || body;
          setStats({
            totalApplications: d.totalApplications ?? d.total_applications ?? 0,
            activeVisas: d.activeVisas ?? d.active_visas ?? 0,
            teamMembers: d.teamMembers ?? d.team_members ?? 0,
            complianceScore: d.complianceScore ?? d.compliance_score ?? 0,
            totalApplicationsTrend: d.totalApplicationsTrend ?? 0,
            activeVisasTrend: d.activeVisasTrend ?? 0,
            teamMembersTrend: d.teamMembersTrend ?? 0,
            complianceScoreTrend: d.complianceScoreTrend ?? 0,
          });
          statsLoaded = true;

          // Extract team members from enterprise dashboard if available
          if (Array.isArray(d.teamMembersList || d.team)) {
            const teamList = d.teamMembersList || d.team;
            setTeamMembers(teamList.map((m: any, idx: number) => ({
              id: m.id || String(idx + 1),
              name: m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown',
              role: m.role || m.position || 'Team Member',
              applicationCount: m.applicationCount || m.applications || 0,
              avatarColor: PIE_COLORS[idx % PIE_COLORS.length],
            })));
          }
        }
      } catch {
        // silent — try admin fallback
      }

      if (!statsLoaded) {
        try {
          const res = await fetch(`${API_BASE_URL}/dashboard/admin`, { headers });
          if (res.ok) {
            const body = await res.json();
            const d = body.data || body;
            setStats({
              totalApplications: d.totalApplications ?? 0,
              activeVisas: d.approvedApplications ?? d.activeVisas ?? 0,
              teamMembers: d.totalUsers ?? 0,
              complianceScore: d.complianceScore ?? 0,
              totalApplicationsTrend: d.totalApplicationsTrend ?? 0,
              activeVisasTrend: d.activeVisasTrend ?? 0,
              teamMembersTrend: d.teamMembersTrend ?? 0,
              complianceScoreTrend: d.complianceScoreTrend ?? 0,
            });
            statsLoaded = true;
          }
        } catch {
          // use defaults (all zeros from initial state)
        }
      }

      // --- Recent Applications ---
      let fetchedApps: any[] = [];
      try {
        const res = await fetch(`${API_BASE_URL}/applications?limit=10`, { headers });
        if (res.ok) {
          const body = await res.json();
          const list = body.data || body.applications || body;
          if (Array.isArray(list) && list.length > 0) {
            fetchedApps = list.slice(0, 10).map((a: any) => ({
              id: a.id || a._id || '',
              applicantName: a.applicantName || a.applicant_name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'N/A',
              destination: a.destination || a.country || '',
              visaType: a.visaType || a.visa_type || a.type || '',
              status: a.status || 'pending',
              submittedAt: a.submittedAt || a.submitted_at || a.createdAt || a.created_at || '',
            }));
            setRecentApps(fetchedApps);
          } else {
            setRecentApps([]);
          }
        } else {
          setRecentApps([]);
        }
      } catch {
        setRecentApps([]);
      }

      // --- Monthly chart data (derived from fetched applications) ---
      const monthMap: Record<string, number> = {};
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = d.toLocaleString('en-US', { month: 'short' });
        monthMap[key] = 0;
      }
      fetchedApps.forEach((app: any) => {
        const appDate = new Date(app.submittedAt || '');
        if (!isNaN(appDate.getTime())) {
          const monthKey = appDate.toLocaleString('en-US', { month: 'short' });
          if (monthKey in monthMap) monthMap[monthKey]++;
        }
      });
      setMonthlyData(Object.entries(monthMap).map(([month, applications]) => ({ month, applications })));

      // --- Status distribution (derived from fetched applications) ---
      const statusCounts: Record<string, number> = {};
      fetchedApps.forEach((app: any) => {
        const status = (app.status || 'pending').toLowerCase();
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      setStatusData(
        Object.entries(statusCounts).map(([name, value]) => ({
          name: name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          value,
          color: STATUS_COLOR_MAP[name] || '#9E9E9E',
        }))
      );

      // --- Team members (if not already loaded from enterprise dashboard) ---
      // team data was set above from enterprise/dashboard if available
    } catch (err) {
      console.error('Enterprise dashboard fetch error:', err);
      showError('Unable to load dashboard data.');
      setRecentApps([]);
      setMonthlyData([]);
      setStatusData([]);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, [token, showError]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Guard
  if (user && user.role !== 'enterprise' && user.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">Access denied. Enterprise privileges are required to view this dashboard.</Alert>
      </Container>
    );
  }

  // ----- RENDER -----
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ================= 1. WELCOME HEADER ================= */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          backgroundImage: 'url(https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          color: '#fff',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          p: 4,
          overflow: 'hidden',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24, fontWeight: 700 }}>
              {user?.firstName?.charAt(0) || 'E'}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                Welcome back, {user?.firstName || 'Enterprise User'}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                {user?.companyName && (
                  <Chip
                    icon={<Business sx={{ color: '#fff !important', fontSize: 16 }} />}
                    label={user.companyName}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 500 }}
                  />
                )}
                <Chip
                  icon={<VerifiedUser sx={{ color: '#fff !important', fontSize: 16 }} />}
                  label="Enterprise"
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Box>
          <Box textAlign="right">
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
              <CalendarToday sx={{ fontSize: 16, opacity: 0.85 }} />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {todayFormatted()}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.25 }}>
              Tripnovac Enterprise
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ================= 2. KPI CARDS ================= */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiSkeleton />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Total Applications"
                value={stats.totalApplications.toLocaleString()}
                trend={stats.totalApplicationsTrend}
                icon={<Assignment sx={{ color: '#fff', fontSize: 28 }} />}
                gradient="linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Active Visas"
                value={stats.activeVisas.toLocaleString()}
                trend={stats.activeVisasTrend}
                icon={<Flight sx={{ color: '#fff', fontSize: 28 }} />}
                gradient="linear-gradient(135deg, #29B848 0%, #4CAF50 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Team Members"
                value={stats.teamMembers.toLocaleString()}
                trend={stats.teamMembersTrend}
                icon={<People sx={{ color: '#fff', fontSize: 28 }} />}
                gradient="linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Compliance Score"
                value={`${stats.complianceScore}%`}
                trend={stats.complianceScoreTrend}
                icon={<Speed sx={{ color: '#fff', fontSize: 28 }} />}
                gradient="linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)"
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* ================= 3. CHARTS ROW ================= */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Area Chart — Applications by Month */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Applications by Month
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1273E5" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#1273E5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.6)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'rgba(255,255,255,0.6)' }} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 2px 12px rgba(0,0,0,0.4)', backgroundColor: '#1E293B', color: '#fff' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="applications"
                    stroke="#1273E5"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorApps)"
                    dot={{ r: 4, fill: '#1273E5' }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Pie Chart — Visa Status Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Visa Status Distribution
            </Typography>
            {loading ? (
              <Skeleton variant="circular" width={220} height={220} sx={{ mx: 'auto', mt: 4 }} />
            ) : statusData.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" height={280}>
                <Typography color="text.secondary">No status data available</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {statusData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color || PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 2px 12px rgba(0,0,0,0.4)', backgroundColor: '#1E293B', color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ================= 4. RECENT APPLICATIONS TABLE ================= */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 4, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Recent Applications
          </Typography>
          <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/applications')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            View All
          </Button>
        </Box>

        {loading ? (
          <Box>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : recentApps.length === 0 ? (
          <Box py={6} textAlign="center">
            <Assignment sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />
            <Typography color="text.secondary">No applications found. Create your first application to get started.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destination</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentApps.map((app) => (
                  <TableRow
                    key={app.id}
                    hover
                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="primary">
                        {app.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#1273E5' }}>
                          {app.applicantName.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{app.applicantName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{app.destination}</TableCell>
                    <TableCell>{app.visaType}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={app.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        color={STATUS_COLORS[app.status] || 'default'}
                        icon={
                          app.status === 'approved' ? (
                            <CheckCircle sx={{ fontSize: '14px !important' }} />
                          ) : app.status === 'pending' || app.status === 'under_review' ? (
                            <HourglassEmpty sx={{ fontSize: '14px !important' }} />
                          ) : app.status === 'rejected' ? (
                            <Cancel sx={{ fontSize: '14px !important' }} />
                          ) : undefined
                        }
                        sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(app.submittedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ================= 5. QUICK ACTIONS ================= */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="New Application"
            description="Start a new visa application for a team member"
            icon={<Add sx={{ color: '#1273E5' }} />}
            onClick={() => navigate('/applications/new')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Bulk Upload"
            description="Upload multiple applications via CSV or Excel"
            icon={<CloudUpload sx={{ color: '#29B848' }} />}
            onClick={() => navigate('/bulk-upload')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Generate Report"
            description="Export compliance and analytics reports"
            icon={<Assessment sx={{ color: '#FF9800' }} />}
            onClick={() => navigate('/enterprise/analytics')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Team Management"
            description="Manage team members, roles, and permissions"
            icon={<GroupWork sx={{ color: '#9C27B0' }} />}
            onClick={() => navigate('/enterprise/team')}
          />
        </Grid>
      </Grid>

      {/* ================= 6. TEAM OVERVIEW ================= */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Team Overview
          </Typography>
          <Chip label={`${teamMembers.length} members`} size="small" color="primary" variant="outlined" />
        </Box>

        {loading ? (
          <Box>
            {[0, 1, 2, 3].map((i) => (
              <Box display="flex" alignItems="center" gap={2} mb={2} key={i}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box flex={1}>
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="25%" />
                </Box>
                <Skeleton variant="rounded" width={60} height={24} />
              </Box>
            ))}
          </Box>
        ) : teamMembers.length === 0 ? (
          <Box py={4} textAlign="center">
            <People sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />
            <Typography color="text.secondary">No team members found. Add members to your enterprise account.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {teamMembers.map((member, idx) => (
              <React.Fragment key={member.id}>
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: member.avatarColor, fontWeight: 600 }}>{member.name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={600}>
                        {member.name}
                      </Typography>
                    }
                    secondary={member.role}
                  />
                  <Tooltip title="Applications managed">
                    <Chip
                      label={`${member.applicationCount} apps`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Tooltip>
                </ListItem>
                {idx < teamMembers.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default EnterpriseDashboard;
